import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TabsContent } from "@/components/ui/tabs";

interface DealCostsTabProps {
  kpis: any;
  onKpisChange: (data: any) => void;
}

export function DealCostsTab({ kpis, onKpisChange }: DealCostsTabProps) {
  const handleOpexChange = (field: string, value: number) => {
    const currentOpex = kpis.opex || {};

    onKpisChange({
      ...kpis,
      opex: {
        ...currentOpex,
        [field]: value,
      },
    });
  };

  const handleRampChange = (field: string, value: any) => {
    const currentRamp = kpis.ramp || {};

    onKpisChange({
      ...kpis,
      ramp: {
        ...currentRamp,
        [field]: value,
      },
    });
  };

  const handleRampSettingsChange = (field: string, value: any) => {
    const currentRampSettings = kpis.rampSettings || {};

    onKpisChange({
      ...kpis,
      rampSettings: {
        ...currentRampSettings,
        [field]: value,
      },
    });
  };

  const handleApplyCostRampChange = (field: string, value: boolean) => {
    const currentRampSettings = kpis.rampSettings || {};
    const currentApplyCostRamp = currentRampSettings.applyCostRamp || {};

    onKpisChange({
      ...kpis,
      rampSettings: {
        ...currentRampSettings,
        applyCostRamp: {
          ...currentApplyCostRamp,
          [field]: value,
        },
      },
    });
  };

  const revenueRampScenarios = {
    conservative: [70, 80, 90, 100],
    standard: [80, 90, 100, 100],
    ambitious: [85, 100, 100, 100],
  };

  const costRampScenarios = {
    conservative: [115, 110, 100, 100],
    standard: [110, 105, 100, 100],
    ambitious: [108, 102, 100, 100],
  };

  return (
    <TabsContent value="costs" className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Operating Expenses (OPEX)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                Departmental Costs
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Rooms Commission (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={kpis.opex?.roomsCommission ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("roomsCommission", 0);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("roomsCommission", numValue);
                        }
                      }
                    }}
                    placeholder="15"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">
                    Guest Supplies (per room night)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={kpis.opex?.guestSuppliesCleaning ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("guestSuppliesCleaning", 0);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("guestSuppliesCleaning", numValue);
                        }
                      }
                    }}
                    placeholder="8"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">F&B COGS (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={kpis.opex?.cogs ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("cogs", 0);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("cogs", numValue);
                        }
                      }
                    }}
                    placeholder="30"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                Other Direct Costs
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm">Meeting & Events (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={kpis.opex?.meetingsEvents ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("meetingsEvents", 0);
                      } else {
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("meetingsEvents", numValue);
                        }
                      }
                    }}
                    placeholder="2"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">
                    Wellness & Other (Fixed/year)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={kpis.opex?.wellnessOther ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("wellnessOther", 0);
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("wellnessOther", numValue);
                        }
                      }
                    }}
                    placeholder="120000"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Other Direct (Fixed/year)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={kpis.opex?.otherDirect ?? ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "") {
                        handleOpexChange("otherDirect", 0);
                      } else {
                        const numValue = parseInt(value);
                        if (!isNaN(numValue) && numValue >= 0) {
                          handleOpexChange("otherDirect", numValue);
                        }
                      }
                    }}
                    placeholder="2000"
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Indirect Costs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                Operating Costs
              </h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Admin & General (%)</Label>
                  <Input
                    type="number"
                    value={kpis.opex?.adminGeneralOther || 4}
                    onChange={(e) =>
                      handleOpexChange(
                        "adminGeneralOther",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">
                    Tech Subscriptions (Fixed/year)
                  </Label>
                  <Input
                    type="number"
                    value={kpis.opex?.techSubscriptions || 1200}
                    onChange={(e) =>
                      handleOpexChange(
                        "techSubscriptions",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Sales & Marketing (%)</Label>
                  <Input
                    type="number"
                    value={kpis.opex?.salesMarketingOther || 3}
                    onChange={(e) =>
                      handleOpexChange(
                        "salesMarketingOther",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-medium text-sm text-muted-foreground">
                Property Costs
              </h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm">Maintenance (%)</Label>
                  <Input
                    type="number"
                    value={kpis.opex?.maintenanceOther || 2}
                    onChange={(e) =>
                      handleOpexChange(
                        "maintenanceOther",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Utilities (%)</Label>
                  <Input
                    type="number"
                    value={kpis.opex?.utilities || 3}
                    onChange={(e) =>
                      handleOpexChange("utilities", Number(e.target.value))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">Management Fee (%)</Label>
                  <Input
                    type="number"
                    value={kpis.opex?.mgmtFee || 3}
                    onChange={(e) =>
                      handleOpexChange("mgmtFee", Number(e.target.value))
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Ramp-up & Growth Assumptions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label className="font-medium">Revenue Ramp-up Scenario</Label>
              <Select
                value={kpis.rampSettings?.revenueRampScenario || "standard"}
                onValueChange={(value) => {
                  handleRampSettingsChange(
                    "revenueRamp",
                    revenueRampScenarios[
                      value as keyof typeof revenueRampScenarios
                    ],
                  );
                  handleRampSettingsChange("revenueRampScenario", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conservative">
                    Conservative (70-80-90-100%)
                  </SelectItem>
                  <SelectItem value="standard">
                    Standard (80-90-100-100%)
                  </SelectItem>
                  <SelectItem value="ambitious">
                    Ambitious (85-100-100-100%)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label className="font-medium">Cost Ramp-up Scenario</Label>
              <Select
                value={kpis.rampSettings?.costRampScenario || "standard"}
                onValueChange={(value) => {
                  handleRampSettingsChange(
                    "costRamp",
                    costRampScenarios[value as keyof typeof costRampScenarios],
                  );
                  handleRampSettingsChange("costRampScenario", value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="conservative">
                    Conservative (115-110-100-100%)
                  </SelectItem>
                  <SelectItem value="standard">
                    Standard (110-105-100-100%)
                  </SelectItem>
                  <SelectItem value="ambitious">
                    Ambitious (108-102-100-100%)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Topline Growth (%/year)</Label>
              <Input
                type="number"
                value={kpis.ramp?.revenueGrowthY2 || 3}
                onChange={(e) =>
                  handleRampChange("revenueGrowthY2", Number(e.target.value))
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Inflation (%/year)</Label>
              <Input
                type="number"
                value={kpis.ramp?.inflationRate || 5}
                onChange={(e) =>
                  handleRampChange("inflationRate", Number(e.target.value))
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Depreciation (% of CapEx)</Label>
              <Input
                type="number"
                value={kpis.rampSettings?.depreciationPctOfCapex || 3}
                onChange={(e) =>
                  handleRampSettingsChange(
                    "depreciationPctOfCapex",
                    Number(e.target.value),
                  )
                }
              />
            </div>
          </div>

          <div className="space-y-4">
            <Label className="font-medium">Apply Cost Ramp to:</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="payroll"
                  checked={kpis.rampSettings?.applyCostRamp?.payroll ?? true}
                  onChange={(e) =>
                    handleApplyCostRampChange("payroll", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="payroll">Payroll</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="departmental"
                  checked={
                    kpis.rampSettings?.applyCostRamp?.departmental ?? true
                  }
                  onChange={(e) =>
                    handleApplyCostRampChange("departmental", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="departmental">Departmental</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="otherOpex"
                  checked={kpis.rampSettings?.applyCostRamp?.otherOpex ?? true}
                  onChange={(e) =>
                    handleApplyCostRampChange("otherOpex", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="otherOpex">Other OPEX</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="undistributed"
                  checked={
                    kpis.rampSettings?.applyCostRamp?.undistributed ?? true
                  }
                  onChange={(e) =>
                    handleApplyCostRampChange("undistributed", e.target.checked)
                  }
                  className="h-4 w-4"
                />
                <Label htmlFor="undistributed">Undistributed</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TabsContent>
  );
}
